import React from 'react';
import imageUrlBuilder from '@sanity/image-url';
import dynamic from 'next/dynamic';
import ReactPlayer from 'react-player/lazy';

import { toKey, types } from '@tmap/mmm-core/entityKey';
import { ArrowForward, PlayButton } from '@tmap/mmm-style-guide/src/Icon';
import { Box } from '@tmap/mmm-style-guide/src/Box';
import { Divider } from '@tmap/mmm-style-guide/src/Divider';
import { Grid } from '@tmap/mmm-style-guide/src/Grid';
import { Hyperlink } from '@tmap/mmm-style-guide/src/Hyperlink';
import { styled } from '@tmap/mmm-style-guide/src/styled';
import { Typography } from '@tmap/mmm-style-guide/src/Typography';
import useFormattedDate from '@tmap/mmm-style-guide/src/ssr/useFormattedDate';

import client from '../client';
import FullWrapper from '../layouts/fullWrapper';
import BlockContent from './block-content';
import Metatags from './metatags';
import CommentsWidget from './commentsWidget';
import ContentCard from './contentCard';
import ResponsivePicture from './responsivePicture';
import CardAd from './cardAd';
import useCurrentUser from '../hooks/useCurrentUser';
import ColorBlocks from '../layouts/colorBlocks';

const DynamicShareBar = dynamic(() => import('./shareBar'), { ssr: false });
const builder = imageUrlBuilder(client);

const StickyShareBar = styled(DynamicShareBar)(({ theme }) => ({
  [theme.breakpoints.down('md')]: {
    display: 'none',
  },
  [theme.breakpoints.up('md')]: {
    marginLeft: '-10vw',
    marginRight: '10vw',
  },
  [theme.breakpoints.up('lg')]: {
    marginLeft: '-120px',
    marginRight: '120px',
  },
  position: 'sticky',
  top: '50px',
  width: 0,
  height: '300px',
  paddingTop: theme.spacing(5),
}));

const ArticleBody = styled(Grid)(({ theme }) => ({
  maxWidth: '750px',
  marginLeft: 'auto',
  marginRight: 'auto',
  paddingLeft: theme.spacing(3),
  paddingRight: theme.spacing(3),
}));

const ShareBarWrapper = styled(Grid)(({ theme }) => ({
  paddingTop: theme.spacing(4.5),
  paddingBottom: theme.spacing(4),
}));

const ContentWrapper = styled(Grid)(({ theme }) => ({
  position: 'relative',
  paddingTop: theme.spacing(4),
  paddingBottom: theme.spacing(4),
}));

const StyledLink = styled(Hyperlink)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  fontSize: '15px',
  color: theme.palette.secondary.main,
  textDecoration: 'underline',
  '&:hover': {
    textDecoration: 'none',
  },
  '&:visited': {
    color: theme.palette.primary.light,
  },
}));

const PlayButtonIcon = styled(PlayButton)(({ theme }) => ({
  fontSize: '96px',
  position: 'absolute',
  [theme.breakpoints.down('md')]: {
    fontSize: '64px',
  },
}));

function HeroMedia(props) {
  const {
    media,
  } = props;

  if (!media) return null;

  const image = media._type === 'videoBlock'
    ? media.previewImage
    : media;

  if (media._type === 'videoBlock') {
    const preview = image ? (
      <ResponsivePicture
        preload
        image={image}
        width={375}
        height={180}
        sm={4}
        md={4}
        style={{
          width: '100%',
          maxWidth: '1020px',
          height: 'auto',
          objectFit: 'cover',
          borderRadius: '8px',
        }}
      />
    ) : true;
    return (
      <Box sx={{
        maxWidth: '100%',
        height: {
          xs: '176px',
          sm: '263px',
          md: '489px',
        },
        width: {
          xs: '375px',
          sm: '560px',
          md: '1020px',
        },
        margin: '0 auto',
      }}
      >
        <ReactPlayer
          url={media.url}
          light={preview}
          playing
          playIcon={<PlayButtonIcon />}
          width='100%'
          height='100%'
        />
      </Box>
    );
  }
  return image && (
    <ResponsivePicture
      preload
      image={image}
      style={{
        margin: 'auto',
        width: '100%',
        maxWidth: '1020px',
        height: 'auto',
        minHeight: '336px',
        maxHeight: '480px',
        objectFit: 'cover',
        borderRadius: '8px',
      }}
      width={375}
      height={180}
      sm={4}
      md={4}
    />
  );
}

function ContentPage(props) {
  const {
    article,
    contentSettings,
    canonicalUrl,
    relatedArticles = [],
  } = props;
  const image = article.heroImage;
  const user = useCurrentUser();
  const createdAt = useFormattedDate(article.displayDate, 'MMM d, yyyy');

  return (
    <>
      <Metatags
        title={article.title}
        description={article.description || ''}
        canonical={canonicalUrl}
        {...(article.heroVideo ? { video: article.heroVideo.url } : {})}
        {...(article.heroImage ? { image: builder.image(image).url() } : {})}
      />
      <FullWrapper>
        <ArticleBody container sx={{ marginTop: '64px', textAlign: 'left' }}>
          <Grid item xs={12}>
            <Typography variant='h3' component='h1' color='dark' gutterBottom fontWeight={600}>{article.title}</Typography>
            {article.byline && (
              <Typography variant='caption' color='textSecondary' paddingBottom={6}>
                By {article.byline}
                &nbsp;&bull;&nbsp;
                {createdAt}
              </Typography>
            )}
          </Grid>
          <Grid item xs={12}>
            <ShareBarWrapper container alignItems='center' spacing={1}>
              {article.pressName && (
                <Grid item>
                  <Typography variant='body2'>{article.pressName}&nbsp;&nbsp;&nbsp;</Typography>
                </Grid>
              )}
              <Grid item sx={{ minHeight: '34.5px' }}>
                <DynamicShareBar
                  url={canonicalUrl}
                  entityKey={toKey(types.ARTICLE, article._id)}
                />
              </Grid>
            </ShareBarWrapper>
          </Grid>
        </ArticleBody>
        <Grid container sx={{ paddingLeft: 3, paddingRight: 3, textAlign: 'center' }}>
          <Grid item xs={12}>
            <HeroMedia
              media={article.heroVideo || article.heroImage}
            />
          </Grid>
        </Grid>
        <ArticleBody container sx={{ position: 'relative' }}>
          <StickyShareBar
            url={canonicalUrl}
            item
            spacing={3}
          />
          <ContentWrapper item xs>
            <BlockContent
              value={article.body}
              pageId='content'
              nested
            />
          </ContentWrapper>
        </ArticleBody>

        <ArticleBody container sx={{ marginBottom: 5 }}>
          {!article.hideAboutUs && (
            <Grid item xs={12}>
              <Divider sx={{ marginBottom: 4.5 }} />
              <Typography variant='subtitle2' sx={{ marginBottom: '14px' }}>{contentSettings.aboutUsHeader}</Typography>
              <Typography variant='body2' color='textSecondary'>{contentSettings.aboutUsCopy}</Typography>
            </Grid>
          )}
          <Grid item xs={12} sx={{ marginTop: 7.5, '& > .MuiBox-root': { padding: 0 } }}>
            {!user._id && (
              <ColorBlocks.Interstitial colorTop='light' colorBottom='light'>
                <CardAd
                  leadCopy={contentSettings.notificationBlockLead}
                  subleadCopy={contentSettings.notificationBlockSubLead}
                  mobile
                />
              </ColorBlocks.Interstitial>
            )}
            <CommentsWidget
              contentId={article._id}
              useShowCommentsToggle={false}
            />
          </Grid>
        </ArticleBody>

        {relatedArticles?.length && (
          <Box sx={{ width: '100%', display: 'flex', justifyContent: 'center' }}>
            <Grid container spacing={2} sx={{ width: '100%', maxWidth: '1024px', padding: 3 }}>
              <Grid item container xs={12}>
                <Grid item xs>
                  <Typography variant='h5' fontWeight={600}>
                    Related Articles
                  </Typography>
                </Grid>
                <Grid item>
                  <StyledLink href='/articles' fontWeight={500}>
                    All Articles <ArrowForward sx={{ ml: 1.5, fontSize: '16px' }} />
                  </StyledLink>
                </Grid>
              </Grid>
              {relatedArticles.map((a) => (
                <Grid item xs={12} md={12 / relatedArticles.length} key={a._id}>
                  <ContentCard article={a} variant='floating' hideType={['communityArticles'].includes(a._type)} />
                </Grid>
              ))}
            </Grid>
          </Box>
        )}
      </FullWrapper>
    </>
  );
}

export default ContentPage;
